import { useCallback, useEffect } from "react";
import { FieldErrors, FieldPath } from "react-hook-form";

type TabFieldsMapping<T> = {
  [tabId: string]: readonly (keyof T)[];
};

type TabConfig = {
  id: string;
  priority?: number;
};

interface UseTabErrorNavigationProps<T extends Record<string, unknown>> {
  errors: FieldErrors<T>;
  tabFieldsMapping: TabFieldsMapping<T>;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  tabs?: TabConfig[];
}

export const useTabErrorNavigation = <T extends Record<string, unknown>>({
  errors,
  tabFieldsMapping,
  currentTab,
  setCurrentTab,
  tabs = [],
}: UseTabErrorNavigationProps<T>) => {
  const fieldToTabMapping = useCallback(() => {
    const mapping: Record<string, string> = {};

    Object.entries(tabFieldsMapping).forEach(([tabId, fields]) => {
      fields.forEach((field) => {
        mapping[field as string] = tabId;
      });
    });

    return mapping;
  }, [tabFieldsMapping]);

  const navigateToFirstErrorTab = useCallback(() => {
    const errorFields = Object.keys(errors) as FieldPath<T>[];

    if (errorFields.length === 0) return null;

    const mapping = fieldToTabMapping();

    const tabsWithErrors = errorFields
      .map((field) => mapping[field])
      .filter((tab): tab is string => Boolean(tab))
      .filter((tab, index, arr) => arr.indexOf(tab) === index);

    if (tabsWithErrors.length === 0) return null;

    if (tabs.length > 0) {
      const sortedTabs = tabs
        .filter((tab) => tabsWithErrors.includes(tab.id))
        .sort((a, b) => (a.priority || 0) - (b.priority || 0));

      if (sortedTabs.length > 0) {
        return sortedTabs[0].id;
      }
    }

    return tabsWithErrors[0];
  }, [errors, fieldToTabMapping, tabs]);

  const navigateToFirstTab = useCallback(() => {
    if (tabs.length > 0) {
      const sortedTabs = [...tabs].sort(
        (a, b) => (a.priority || 0) - (b.priority || 0),
      );
      return sortedTabs[0].id;
    }
    return null;
  }, [tabs]);

  const getTabsWithErrors = useCallback(() => {
    const errorFields = Object.keys(errors) as FieldPath<T>[];
    const mapping = fieldToTabMapping();

    return errorFields
      .map((field) => mapping[field])
      .filter((tab): tab is string => Boolean(tab))
      .filter((tab, index, arr) => arr.indexOf(tab) === index);
  }, [errors, fieldToTabMapping]);

  const tabHasErrors = useCallback(
    (tabId: string) => {
      return getTabsWithErrors().includes(tabId);
    },
    [getTabsWithErrors],
  );

  useEffect(() => {
    const firstErrorTab = navigateToFirstErrorTab();

    if (firstErrorTab && firstErrorTab !== currentTab) {
      const currentTabHasErrors = Object.keys(errors).some(
        (field) => fieldToTabMapping()[field as FieldPath<T>] === currentTab,
      );

      if (!currentTabHasErrors) {
        setCurrentTab(firstErrorTab);
      }
    }
  }, [
    errors,
    currentTab,
    setCurrentTab,
    navigateToFirstErrorTab,
    fieldToTabMapping,
  ]);

  return {
    navigateToFirstErrorTab,
    navigateToFirstTab,
    hasErrors: Object.keys(errors).length > 0,
    getTabsWithErrors,
    tabHasErrors,
  };
};
